import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginActivityApi from '../../baseUrl/LoginActivityApi';
import { useTheme } from '../../App';

interface LoginStatus {
  id: number;
  app_name: string;
  app_status_code: number | string;
  app_status_message: string;
}

interface ErrorNotification {
  id: number;
  appName: string;
  statusCode: number | string;
  message: string;
  isFreshLogin: boolean;
}

const LoginActivityErrorNotification: React.FC = () => {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const [errors, setErrors] = useState<ErrorNotification[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const isDark = resolvedTheme === 'dark';

  const fetchLoginStatuses = useCallback(async () => {
    try {
      const statuses = await LoginActivityApi.getAllLoginStatuses();
      
      if (Array.isArray(statuses)) {
        const errorStatuses: ErrorNotification[] = statuses
          .filter((status: LoginStatus) => {
            const statusCode = status.app_status_code;
            const isFreshLogin = statusCode === '' || statusCode === null || statusCode === undefined;
            const isError = typeof statusCode === 'string' && statusCode !== '' && parseInt(statusCode, 10) >= 400;
            const isErrorNum = typeof statusCode === 'number' && (statusCode >= 400 || statusCode === 0);
            return isFreshLogin || isError || isErrorNum;
          })
          .map((status: LoginStatus) => {
            const isFreshLogin = status.app_status_code === '' || status.app_status_code === null || status.app_status_code === undefined;
            return {
              id: status.id,
              appName: status.app_name,
              statusCode: isFreshLogin ? '' : status.app_status_code,
              message: status.app_status_message || '',
              isFreshLogin
            };
          });

        setErrors(errorStatuses);
        setIsVisible(errorStatuses.length > 0);
      }
    } catch (error) {
      console.error('Error fetching login statuses:', error);
    }
  }, []);

  useEffect(() => {
    fetchLoginStatuses();
    const interval = setInterval(fetchLoginStatuses, 30000);
    return () => clearInterval(interval);
  }, [fetchLoginStatuses]);

  const handleGoToActivity = () => {
    navigate('/app-login-activity');
    setIsExpanded(false);
  };

  // Warning Triangle Icon - Yellow with black exclamation
  const WarningTriangle = ({ size = 24 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path 
        d="M12 2.5c-.7 0-1.3.4-1.7 1L2.5 17.5c-.7 1.2.2 2.5 1.7 2.5h15.6c1.5 0 2.4-1.3 1.7-2.5L13.7 3.5c-.4-.6-1-.9-1.7-1z" 
        fill="#FBBF24"
      />
      <path 
        d="M12 8v5" 
        stroke="#000" 
        strokeWidth="2" 
        strokeLinecap="round"
      />
      <circle cx="12" cy="16" r="1.2" fill="#000"/>
    </svg>
  );

  if (!isVisible || errors.length === 0) {
    return null;
  }

  // Dynamic styles based on theme
  const panelBg = isDark ? '#1f2937' : '#ffffff';
  const borderColor = isDark ? '#374151' : '#e5e7eb';
  const textPrimary = isDark ? '#f9fafb' : '#111827';
  const textSecondary = isDark ? '#9ca3af' : '#6b7280';
  const badgeBg = isDark ? '#374151' : '#f3f4f6';
  const badgeText = isDark ? '#d1d5db' : '#4b5563';

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
      {/* Shake Animation Style */}
      <style>
        {`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            10%, 30%, 50%, 70%, 90% { transform: translateX(-3px); }
            20%, 40%, 60%, 80% { transform: translateX(3px); }
          }
          .error-shake {
            animation: shake 0.6s ease-in-out infinite;
            animation-delay: 2s;
          }
          .error-shake:hover {
            animation: none;
          }
        `}
      </style>

      {/* Expanded Panel - Compact Size */}
      {isExpanded && (
        <div className="absolute bottom-12 sm:bottom-14 right-0 w-[calc(100vw-2rem)] sm:w-72 max-w-72 overflow-hidden mb-2">
          <div 
            className="rounded-xl overflow-hidden"
            style={{ 
              backgroundColor: panelBg,
              boxShadow: isDark ? '0 8px 24px rgba(0, 0, 0, 0.4)' : '0 8px 24px rgba(0, 0, 0, 0.12)',
              border: `1px solid ${borderColor}`
            }}
          >
            {/* Header */}
            <div 
              className="px-3 py-2.5 flex items-center justify-between"
              style={{ borderBottom: `1px solid ${borderColor}` }}
            >
              <div className="flex items-center gap-2">
                <WarningTriangle size={18} />
                <span style={{ color: textPrimary }} className="font-semibold text-sm">
                  ERROR ({errors.length})
                </span>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                style={{ color: textSecondary }}
                className="hover:opacity-80 transition-opacity p-0.5"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Error List */}
            <div className="max-h-40 sm:max-h-48 overflow-y-auto">
              {errors.map((error) => (
                <div
                  key={error.id}
                  className="px-3 py-2.5 last:border-b-0"
                  style={{ borderBottom: `1px solid ${borderColor}` }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span 
                      className="text-xs uppercase font-semibold"
                      style={{ color: textPrimary }}
                    >
                      {error.appName}
                    </span>
                    <span 
                      className="text-xs px-2 py-0.5 rounded-md font-medium"
                      style={{ backgroundColor: badgeBg, color: badgeText }}
                    >
                      {error.isFreshLogin ? '""' : error.statusCode}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: textSecondary }}>
                    {error.message}
                  </p>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div 
              className="px-3 py-2.5"
              style={{ borderTop: `1px solid ${borderColor}` }}
            >
              <button
                onClick={handleGoToActivity}
                className="w-full py-2 text-white text-xs font-semibold rounded-lg transition-all hover:opacity-90 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-sm hover:shadow-md"
              >
                Go to App Login Activity
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Circle Button with Warning Triangle - Smaller */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-105 ${!isExpanded ? 'error-shake' : ''}`}
        style={{ 
          backgroundColor: panelBg,
          boxShadow: isDark ? '0 4px 16px rgba(0, 0, 0, 0.4)' : '0 4px 16px rgba(0, 0, 0, 0.12)',
          border: `1px solid ${borderColor}`
        }}
        title={`${errors.length} error(s) detected`}
      >
        <div className="relative">
          <WarningTriangle size={24} />
          {/* Error Count Badge */}
          <span 
            className="absolute -top-1.5 -right-2.5 w-5 h-5 text-white text-[10px] font-bold rounded-full flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600"
            style={{ boxShadow: '0 2px 6px rgba(59, 130, 246, 0.4)' }}
          >
            {errors.length}
          </span>
        </div>
      </button>
    </div>
  );
};

export default LoginActivityErrorNotification;
